import { formatLocalDate } from '../utils/localDateTime';
import { confirmApp } from '../common/appDialog';
import React, { useState, useEffect } from 'react';
import { UserProfile, Organization, SubscriptionTier } from '@types';
import { db, functions } from '../../firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getPaddleClientConfiguration, getPaddleTierPriceIds, openCheckout, openCustomerPortal } from '../../services/paddleService';
import { addOrganizationLicense, billingErrorMessage } from '../../services/organizationLicenseService';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';
import { subscriptionStatusLabel } from '../utils/subscriptionStatusLabel';

interface SubscriptionManagementProps {
    currentUser: UserProfile;
    currentOrganization: Organization;
    onBack: () => void;
}

// ─── Canonical pricing (matches SubscriptionSetup.tsx) ───────────────────────
const TIER_PRICES = {
    individual: { monthly: 99,  annual: 79  },
    business:   { monthly: 349, annual: 279 },
    agency:     { monthly: 649, annual: 519 },
} as const;

const ADDON_PRICES = {
    extraAdmin:  { monthly: 55 },
    extraUser:   { monthly: 35 },
    extraClient: { monthly: 99 },
} as const;

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({
    currentUser,
    currentOrganization,
    onBack,
}) => {
    const [organization, setOrganization] = useState<Organization>(currentOrganization);
    const [activeBillingAction, setActiveBillingAction] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [extraClientPriceId, setExtraClientPriceId] = useState('');
    const [paddlePortalUrl, setPaddlePortalUrl] = useState('');

    // ── Live Firestore listener so the tier badge updates when webhook fires ──
    useEffect(() => {
        void getPaddleClientConfiguration()
            .then(configuration => {
                setExtraClientPriceId(configuration.extraClientPriceId);
                setPaddlePortalUrl(configuration.portalUrl);
            })
            .catch(failure => console.error('Paddle configuration failed loading.', failure));
        if (!db || !currentOrganization?.id) return;

        const orgRef = doc(db, 'organizations', currentOrganization.id);
        const unsubscribe = onSnapshot(orgRef, (snap) => {
            if (snap.exists()) {
                setOrganization({ id: snap.id, ...snap.data() } as Organization);
            } else {
                setError('Organization data not found.');
            }
        }, (e) => {
            console.error('Org listener error:', e);
            setError('Failed to load subscription details.');
        });

        return () => unsubscribe();
    }, [currentOrganization.id]);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const tier = (organization.tier || 'individual') as SubscriptionTier;
    const isLoading = activeBillingAction !== null;
    const isActive = ['active', 'on_trial', 'past_due', 'paused', 'paid', 'trialing'].includes(
        organization.subscriptionStatus || ''
    );

    // The webhook writes paddleCustomerId directly onto the org doc (flat field).
    // Fall back through multiple possible paths for legacy accounts.
    const paddleCustomerId =
        (organization as any).paddleCustomerId ||
        (organization as any).subscription?.paddleCustomerId ||
        (organization as any).billing?.paddleCustomerId ||
        (organization as any).billing?.customerId ||
        null;

    const paddleSubscriptionId =
        (organization as any).paddleSubscriptionId ||
        (organization as any).subscription?.paddleSubscriptionId ||
        (organization as any).billing?.paddleSubscriptionId ||
        null;

    // A portal can be opened with either a customer ID or subscription ID
    const hasBillingProfile = !!(paddleCustomerId || paddleSubscriptionId);

    // ── Paddle helpers ────────────────────────────────────────────────────────
    const launchCheckout = async (priceId: string, planType: string, updateType: string) => {
        if (!priceId) {
            setError(`Configuration Error: Price ID for ${planType} is not configured. Contact support.`);
            return;
        }
        setActiveBillingAction(updateType);
        setError(null);
        try {
            await openCheckout({
                items: [{ priceId, quantity: 1 }],
                customer: { email: currentUser.email },
                customData: {
                    organization_id: organization.id,
                    user_id: currentUser.uid,
                    update_type: updateType,
                    plan_type: planType,
                },
                settings: {
                    displayMode: 'overlay',
                    theme: 'dark',
                    showAddDiscounts: true,
                },
            });
        } catch (err: any) {
            console.error('Paddle checkout failed:', err);
            setError('Failed to initialize checkout. Please try again.');
        } finally {
            setActiveBillingAction(null);
        }
    };

    const handleUpgrade = async (target: SubscriptionTier) => {
        try {
            const prices = await getPaddleTierPriceIds();
            const priceId =
                target === 'business' ? prices.business :
                target === 'agency'   ? prices.agency   :
                                        prices.individual;
            await launchCheckout(priceId, target, `upgrade_to_${target}`);
        } catch (error: any) {
            setError(error.message || 'Product mappings failed loading.');
        }
    };

    const handleAddSeat = async (type: 'admin' | 'user') => {
        setActiveBillingAction(`add-${type}`);
        setError(null);
        setSuccessMsg(null);
        try {
            await addOrganizationLicense(type);
            setSuccessMsg(`${type === 'admin' ? 'Admin' : 'User'} license added successfully.`);
        } catch (error: any) {
            setError(billingErrorMessage(error));
        } finally {
            setActiveBillingAction(null);
        }
    };

    const handleAddClientSlot = () => {
        launchCheckout(extraClientPriceId, 'extra_client', 'add_client_slot');
    };

    const handleManageBilling = async () => {
        if (!hasBillingProfile) {
            setError(
                'No billing profile found. Please contact contact@eprims.com to manage your subscription.'
            );
            return;
        }
        try {
            if (paddleCustomerId) {
                await openCustomerPortal(paddleCustomerId);
            } else if (paddleSubscriptionId) {
                // Legacy accounts: open portal via subscription ID
                await openCustomerPortal(paddleSubscriptionId);
            }
        } catch {
            setError('Could not open billing portal. Please try again.');
        }
    };

    const handleCancelSubscription = async () => {
        if (
            !await confirmApp('Your organization keeps full access through the current paid period. Future renewals will stop.', {
                title: 'Cancel Subscription',
                confirmLabel: 'Schedule Cancellation',
                tone: 'danger',
            })
        )
            return;
        if (!functions)
            return;
        setActiveBillingAction('cancel');
        setError(null);
        try {
            const cancelSubscription = httpsCallable(functions, 'cancelOrganizationSubscription');
            const response = await cancelSubscription({});
            const effectiveAt = String((response.data as any)?.effectiveAt || '');
            setSuccessMsg(effectiveAt
                ? `Cancellation scheduled. Access continues through ${formatLocalDate(effectiveAt)}.`
                : 'Cancellation scheduled for the paid period end.');
        } catch (error: any) {
            setError(error?.message || 'Cancellation could not be scheduled.');
        } finally {
            setActiveBillingAction(null);
        }
    };

    // ── Tier badge colours ────────────────────────────────────────────────────
    const tierColour =
        tier === 'agency'   ? 'bg-violet-100 text-violet-700 border-violet-200' :
        tier === 'business' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                              'bg-slate-100 text-slate-700 border-slate-200';

    return (
        <div className="min-h-screen bg-slate-50 p-8">
            <div className="max-w-4xl mx-auto animate-fade-in">

                {/* ── Header ───────────────────────────────────────────────── */}
                <button
                    onClick={onBack}
                    className="flex items-center text-slate-500 hover:text-blue-600 mb-8 text-sm font-medium transition-colors"
                >
                    <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Dashboard
                </button>

                <div className="flex items-start justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-800">Subscription Management</h1>
                        <p className="text-slate-500 mt-1 text-sm">Manage your plan, seats, and billing details.</p>
                    </div>
                    <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide border ${tierColour}`}>
                        {tier}
                    </span>
                </div>

                {/* ── Notifications ─────────────────────────────────────────── */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-xl mb-6 flex items-start gap-3">
                        <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                            <p className="font-bold text-sm">Error</p>
                            <p className="text-sm mt-0.5">{error}</p>
                        </div>
                        <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
                    </div>
                )}
                {successMsg && (
                    <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-5 py-4 rounded-xl mb-6">
                        <p className="text-sm font-medium">{successMsg}</p>
                    </div>
                )}

                {/* ── Current Plan Card ──────────────────────────────────────── */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 mb-6">
                    <h2 className="text-lg font-bold text-slate-700 mb-6">Current Plan</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Plan</p>
                            <p className="text-2xl font-extrabold text-slate-800 capitalize">{tier}</p>
                            <p className="text-sm text-slate-500 mt-1">
                                ${TIER_PRICES[tier]?.monthly ?? '—'}/mo · ${TIER_PRICES[tier]?.annual ?? '—'}/mo billed annually
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Status</p>
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${
                                isActive ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-amber-100 text-amber-700 border border-amber-200'
                            }`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`} />
                                {subscriptionStatusLabel(organization.subscriptionStatus, organization.isTestOrganization, 'Inactive')}
                            </span>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Organization</p>
                            <p className="text-sm font-bold text-slate-700">{getOrganizationDisplayName(organization)}</p>
                            <p className="text-xs text-slate-400 font-mono mt-0.5">{organization.id}</p>
                        </div>
                    </div>
                </div>

                {/* ── Seats & Add-ons ───────────────────────────────────────── */}
                {(tier === 'business' || tier === 'agency') && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 mb-6">
                        <h2 className="text-lg font-bold text-slate-700 mb-6">Seats & Add-ons</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                            {/* Admin seats */}
                            <div className="p-5 rounded-xl bg-slate-50 border border-slate-200">
                                <div className="flex justify-between items-center mb-3">
                                    <p className="text-sm font-bold text-slate-700">Admin Licenses</p>
                                    <span className="text-xs text-slate-500">{organization.totalAdmins || 1} included</span>
                                </div>
                                <p className="text-xs text-slate-500 mb-4">Full access to SRM Architect and all intelligence suites.</p>
                                <button
                                    onClick={() => handleAddSeat('admin')}
                                    disabled={isLoading}
                                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-bold py-2 px-4 rounded-lg transition-colors"
                                >
                                    {activeBillingAction === 'add-admin' ? 'Processing...' : '+ Add Admin License'}
                                </button>
                            </div>

                            {/* User seats */}
                            <div className="p-5 rounded-xl bg-slate-50 border border-slate-200">
                                <div className="flex justify-between items-center mb-3">
                                    <p className="text-sm font-bold text-slate-700">User Licenses</p>
                                    <span className="text-xs text-slate-500">{organization.totalUsers || 0} included</span>
                                </div>
                                <p className="text-xs text-slate-500 mb-4">Read-only access to intelligence feeds and mapping tools.</p>
                                <button
                                    onClick={() => handleAddSeat('user')}
                                    disabled={isLoading}
                                    className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold py-2 px-4 rounded-lg transition-colors"
                                >
                                    {activeBillingAction === 'add-user' ? 'Processing...' : '+ Add User License'}
                                </button>
                            </div>

                            {/* Client slots — Agency only */}
                            {tier === 'agency' && (
                                <div className="p-5 rounded-xl bg-violet-50 border border-violet-200 md:col-span-2">
                                    <div className="flex justify-between items-center mb-3">
                                        <p className="text-sm font-bold text-violet-800">Client Workspace Slots</p>
                                        <span className="text-xs text-violet-600">{organization.maxClientSlots || 5} slots included</span>
                                    </div>
                                    <p className="text-xs text-violet-700 mb-4">Each slot allows you to onboard and manage one client programme with dedicated intelligence workspaces.</p>
                                    <button
                                        onClick={handleAddClientSlot}
                                        disabled={isLoading || !extraClientPriceId}
                                        className="w-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-xs font-bold py-2 px-4 rounded-lg transition-colors"
                                    >
                                        {activeBillingAction === 'add_client_slot' ? 'Processing...' : `+ Add Client Slot ($${ADDON_PRICES.extraClient.monthly}/mo)`}
                                        {!extraClientPriceId && <span className="ml-2 opacity-60">(Contact us)</span>}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Upgrade Paths ─────────────────────────────────────────── */}
                {(tier === 'individual' || tier === 'business') && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 mb-6">
                        <h2 className="text-lg font-bold text-slate-700 mb-2">Upgrade Your Plan</h2>
                        <p className="text-sm text-slate-500 mb-6">Unlock more capabilities for your team or client programmes.</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {tier === 'individual' && (
                                <div className="p-6 rounded-xl border-2 border-blue-200 bg-blue-50">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <p className="font-black text-blue-800 text-lg">Business Intelligence</p>
                                            <p className="text-2xl font-extrabold text-blue-700">${TIER_PRICES.business.monthly}<span className="text-sm font-normal">/mo</span></p>
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-widest text-blue-500 bg-blue-100 px-2 py-1 rounded-full border border-blue-200">Recommended</span>
                                    </div>
                                    <ul className="space-y-1.5 mb-5">
                                        {['Team seat management', 'Admin & user license add-ons', 'Co-branding on reports', 'Priority support'].map(f => (
                                            <li key={f} className="flex items-center gap-2 text-xs text-blue-700">
                                                <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => handleUpgrade('business')}
                                        disabled={isLoading}
                                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-sm transition-colors"
                                    >
                                        {activeBillingAction === 'upgrade_to_business' ? 'Processing...' : 'Upgrade to Business →'}
                                    </button>
                                </div>
                            )}

                            {/* Agency upgrade — Coming Soon */}
                            <div className="p-6 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 relative overflow-hidden">
                                <div className="absolute top-3 right-3">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 bg-white border border-slate-200 px-2.5 py-1 rounded-full">Coming Soon</span>
                                </div>
                                <div className="flex items-start gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center shrink-0">
                                        <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="font-black text-slate-500 text-lg">Agency Platform</p>
                                        <p className="text-2xl font-extrabold text-slate-300">$649<span className="text-sm font-normal">/mo</span></p>
                                    </div>
                                </div>
                                <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                                    Multi-client workspaces, white-label intelligence reports, Agency Hub dashboard, and dedicated account management.
                                </p>
                                <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                                    <p className="text-xs font-bold text-slate-500 text-center">
                                        📢 Look out for the future release of our Agency Business Model &mdash; coming soon.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Billing Management ────────────────────────────────────── */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 mb-6">
                    <h2 className="text-lg font-bold text-slate-700 mb-6">Billing & Payments</h2>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={handleManageBilling}
                            disabled={isLoading || !hasBillingProfile}
                            className="flex-1 bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-bold py-3 px-6 rounded-xl text-sm transition-colors"
                        >
                            Manage Billing & Invoices
                        </button>
                        {isActive && currentUser.role === 'owner' && !organization.cancellationEffectiveAt && (
                            <button
                                onClick={handleCancelSubscription}
                                disabled={isLoading}
                                className="flex-1 bg-white hover:bg-red-50 border border-red-200 text-red-600 hover:text-red-700 disabled:opacity-50 font-bold py-3 px-6 rounded-xl text-sm transition-colors"
                            >
                                {activeBillingAction === 'cancel' ? 'Scheduling...' : 'Cancel Subscription'}
                            </button>
                        )}
                    </div>
                    {!hasBillingProfile && (
                        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                            <p className="text-sm font-semibold text-slate-700 mb-1">Manage Your Subscription</p>
                            <p className="text-xs text-slate-500 mb-3">Your billing profile is still syncing or was created under a legacy account. You can manage your subscription directly via the Paddle portal or contact our team.</p>
                            <div className="flex flex-wrap gap-2">
                                <a
                                    href={paddlePortalUrl || undefined}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-4 py-2 rounded-lg transition-colors"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                    Open Paddle Portal
                                </a>
                                <a
                                    href="mailto:contact@eprims.com?subject=Subscription%20Management%20Request"
                                    className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-800 bg-white hover:bg-slate-50 border border-slate-300 px-4 py-2 rounded-lg transition-colors"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                    Email Support
                                </a>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Footer ────────────────────────────────────────────────── */}
                <div className="flex items-center justify-center gap-2 mt-6 opacity-50">
                    <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <p className="text-[11px] text-slate-400 text-center">
                        Payments and billing are securely managed by{' '}
                        <a href="https://www.paddle.com" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500 underline">Paddle</a>.
                        IntelOptics does not store your payment details.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionManagement;
